// src/components/admin/Notices.tsx
import React from "react";

type Notice = {
  id?: string; // ★ 文字列IDに統一
  title: string;
  body: string;
  level: "info" | "warning" | "urgent";
  publish: boolean;
  pinned: boolean;

  // startAt = 表示開始制御（画面には“表示日時”としては出さない）
  startAt?: string;

  // ★ displayAt = 画面に表示する「お知らせの日付」
  displayAt?: string;

  endAt?: string; // サーバー互換のため型だけ残す
  linkUrl?: string;
  createdAt?: string;
  updatedAt?: string;
};

const API = process.env.REACT_APP_API_BASE_URL || "";
const PER_PAGE = 20; // 1ページあたり20件

// datetime-local 入力用（YYYY-MM-DDTHH:mm）に整形
function toDateTimeLocalValue(value?: string) {
  if (!value) return "";
  let s = String(value).trim();
  if (!s) return "";
  // "YYYY-MM-DD HH:mm" → "YYYY-MM-DDTHH:mm"
  s = s.replace(" ", "T");
  // 秒やZが付いてても入力は分まで
  if (s.includes("T") && s.length >= 16) return s.slice(0, 16);
  // 日付だけなら 00:00 を補完
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00`;
  return s;
}

// 保存用：日付だけなら 00:00 を補完（サーバに投げる値を統一）
function normalizeDateTimeForSave(value?: string) {
  if (!value) return "";
  const s0 = String(value).trim();
  if (!s0) return "";
  const s = s0.replace(" ", "T");
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00`;
  return s;
}

// 画面表示用（YYYY-MM-DD HH:mm っぽく）
function formatDateTime(value?: string) {
  if (!value) return "";
  const s = String(value).trim().replace(" ", "T");
  if (!s) return "";
  if (s.includes("T") && s.length >= 16) return s.slice(0, 16).replace("T", " ");
  return s;
}

export default function Notices() {
  const [rows, setRows] = React.useState<Notice[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [msg, setMsg] = React.useState("");

  // ★ form は datetime-local 形式（YYYY-MM-DDTHH:mm）で保持
  const [form, setForm] = React.useState<Notice>({
    title: "",
    body: "",
    level: "info",
    publish: true,
    pinned: false,
    startAt: "",
    displayAt: "",
    endAt: "",
    linkUrl: "",
  });
  const [editId, setEditId] = React.useState<string | null>(null);

  // ページング
  const [page, setPage] = React.useState(1);

  async function fetchList() {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch(`${API}/api/admin/notices`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      const list = Array.isArray(j) ? j : [];
      setRows(list);
      setPage(1);
    } catch (e: any) {
      setMsg(e.message || "取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    fetchList();
  }, []);

  // rows が変わったらページ番号を補正
  React.useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [rows]);

  function resetForm() {
    setForm({
      title: "",
      body: "",
      level: "info",
      publish: true,
      pinned: false,
      startAt: "",
      displayAt: "",
      endAt: "",
      linkUrl: "",
    });
    setEditId(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg("");
    try {
      const method = editId ? "PUT" : "POST";
      const url = editId
        ? `${API}/api/admin/notices/${encodeURIComponent(editId)}`
        : `${API}/api/admin/notices`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...form,
          // ★ 保存時は統一（YYYY-MM-DDTHH:mm もしくは空）
          startAt: normalizeDateTimeForSave(form.startAt),
          displayAt: normalizeDateTimeForSave(form.displayAt),
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchList();
      resetForm();
      setMsg("保存しました。");
    } catch (e: any) {
      setMsg(e.message || "保存に失敗しました。");
    }
  }

  // 削除
  async function del(id: string) {
    if (!window.confirm("削除しますか？")) return;
    try {
      const res = await fetch(`${API}/api/admin/notices/${encodeURIComponent(id)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // 即時反映
      setRows((prev) => prev.filter((n) => String(n.id) !== String(id)));
    } catch (e: any) {
      window.alert(e.message || "削除に失敗しました。");
    }
  }

  // 公開トグル（PUT /api/admin/notices/:id）
  async function togglePublish(id: string) {
    const target = rows.find((n) => String(n.id) === String(id));
    if (!target) return;
    const next = !target.publish;
    try {
      const res = await fetch(`${API}/api/admin/notices/${encodeURIComponent(id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ publish: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((prev) =>
        prev.map((n) => (String(n.id) === String(id) ? { ...n, publish: next } : n))
      );
    } catch (e: any) {
      console.error(e);
      window.alert("公開切り替えに失敗しました。");
    }
  }

  // ピン留めトグル（PUT /api/admin/notices/:id）
  async function togglePinned(id: string) {
    const target = rows.find((n) => String(n.id) === String(id));
    if (!target) return;
    const next = !target.pinned;
    try {
      const res = await fetch(`${API}/api/admin/notices/${encodeURIComponent(id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pinned: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((prev) =>
        prev.map((n) => (String(n.id) === String(id) ? { ...n, pinned: next } : n))
      );
    } catch (e: any) {
      console.error(e);
      window.alert("ピン留め切り替えに失敗しました。");
    }
  }

  function edit(n: Notice) {
    const idStr = String(n.id ?? "");
    setEditId(idStr || null);
    setForm({
      title: n.title,
      body: n.body,
      level: n.level,
      publish: n.publish,
      pinned: n.pinned,
      // ★ form は datetime-local 形式で保持
      startAt: toDateTimeLocalValue(n.startAt || ""),
      displayAt: toDateTimeLocalValue(n.displayAt || ""),
      endAt: n.endAt || "",
      linkUrl: n.linkUrl || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // ページングされた行
  const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const startIndex = (page - 1) * PER_PAGE;
  const pagedRows = rows.slice(startIndex, startIndex + PER_PAGE);

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "24px auto",
        background: "#fff",
        padding: 16,
        borderRadius: 8,
      }}
    >
      <h2 style={{ marginBottom: 12 }}>お知らせ管理</h2>

      <form
        onSubmit={submit}
        style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr" }}
      >
        {/* タイトル */}
        <div style={{ gridColumn: "1 / -1" }}>
          <label>タイトル</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            style={{ width: "100%" }}
            required
          />
        </div>

        {/* 開始日時（表示開始の制御用：画面“表示日時”ではない） */}
        <div>
          <label>開始日時(任意) ※表示開始</label>
          <input
            type="datetime-local"
            value={form.startAt || ""}
            onChange={(e) => setForm({ ...form, startAt: e.target.value })}
            style={{ width: "100%" }}
          />
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            ※ この日時になったら公開側に出ます（一覧/公開の「日付表示」には使いません）
          </div>
        </div>

        {/* ★ 表示日時（お知らせの日付として画面に表示する用） */}
        <div>
          <label>表示日時(任意) ※画面に表示する日付</label>
          <input
            type="datetime-local"
            value={form.displayAt || ""}
            onChange={(e) => setForm({ ...form, displayAt: e.target.value })}
            style={{ width: "100%" }}
          />
        </div>

        {/* レベル */}
        <div>
          <label>レベル</label>
          <select
            value={form.level}
            onChange={(e) => setForm({ ...form, level: e.target.value as any })}
            style={{ width: "100%" }}
          >
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="urgent">urgent</option>
          </select>
        </div>

        {/* リンクURL */}
        <div>
          <label>リンクURL(任意)</label>
          <input
            value={form.linkUrl}
            onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
            style={{ width: "100%" }}
          />
        </div>

        {/* ダミー（レイアウト調整用スペース） */}
        <div />

        {/* 本文 */}
        <div style={{ gridColumn: "1 / -1" }}>
          <label>本文</label>
          <textarea
            rows={5}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            style={{ width: "100%" }}
          />
        </div>

        {/* 公開・ピン留め */}
        <div>
          <label>
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) => setForm({ ...form, publish: e.target.checked })}
            />{" "}
            公開
          </label>
        </div>

        <div>
          <label>
            <input
              type="checkbox"
              checked={form.pinned}
              onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
            />{" "}
            ピン留め
          </label>
        </div>

        {/* ボタン */}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
          <button
            type="submit"
            style={{
              padding: "8px 16px",
              background: "#1976d2",
              color: "#fff",
              borderRadius: 6,
            }}
          >
            {editId ? "更新" : "作成"}
          </button>
          {editId && (
            <button type="button" onClick={resetForm} style={{ padding: "8px 16px" }}>
              キャンセル
            </button>
          )}
          {msg && <span style={{ marginLeft: 8, color: "#2e7d32" }}>{msg}</span>}
        </div>
      </form>

      <hr style={{ margin: "16px 0" }} />

      <h3>一覧</h3>
      {loading ? (
        <div>読込中…</div>
      ) : (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f2f4f7" }}>
                <th style={{ padding: 8, textAlign: "left" }}>ID</th>
                <th style={{ padding: 8, textAlign: "left" }}>タイトル</th>
                <th style={{ padding: 8 }}>公開</th>
                <th style={{ padding: 8 }}>PIN</th>
                <th style={{ padding: 8 }}>レベル</th>
                <th style={{ padding: 8, textAlign: "left" }}>表示日時</th>
                <th style={{ padding: 8 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((n) => (
                <tr key={n.id}>
                  <td style={{ padding: 8 }}>{n.id}</td>
                  <td style={{ padding: 8 }}>{n.title}</td>
                  <td style={{ padding: 8, textAlign: "center" }}>
                    <button type="button" onClick={() => togglePublish(String(n.id))}>
                      {n.publish ? "公開中" : "非公開"}
                    </button>
                  </td>
                  <td style={{ padding: 8, textAlign: "center" }}>
                    <button type="button" onClick={() => togglePinned(String(n.id))}>
                      {n.pinned ? "PIN" : "-"}
                    </button>
                  </td>
                  <td style={{ padding: 8, textAlign: "center" }}>{n.level}</td>

                  {/* ★ 表示日時だけを出す（startAt は出さない） */}
                  <td style={{ padding: 8 }}>
                    {formatDateTime(n.displayAt) || "-"}
                  </td>

                  <td
                    style={{
                      padding: 8,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => edit(n)}
                      style={{ marginRight: 8 }}
                    >
                      編集
                    </button>
                    <button type="button" onClick={() => del(String(n.id))}>
                      削除
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: 12,
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    データがありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ページネーション */}
          {rows.length > 0 && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 12,
                fontSize: 13,
              }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  padding: "4px 10px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page <= 1 ? "#f5f5f5" : "#fff",
                  cursor: page <= 1 ? "default" : "pointer",
                }}
              >
                前へ
              </button>
              <span>
                {page} / {totalPages} ページ（全 {rows.length} 件）
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{
                  padding: "4px 10px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page >= totalPages ? "#f5f5f5" : "#fff",
                  cursor: page >= totalPages ? "default" : "pointer",
                }}
              >
                次へ
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
